import type { ComposerSource, ComposerState } from '../modules/publishing/composerState';
import { toPostInput } from '../modules/publishing/composerState';
import type { FbContentType, GbpConfig, GbpCtaType, IgContentType, PostInput, PostMedia, PostPlatform } from '../types/publishing';

// KyckSocial composer — the club is a SECOND HOST of the publishing composer's
// state model (`modules/publishing/composerState.ts`: effectiveCaption /
// effectiveMedia / toPostInput / fromPost), not an owner of a parallel one. This
// file is only the ADAPTER at the edge: the club's narrower post projection in,
// the club's save body out. (It replaces Run J's destination/placement lib, which
// modelled the same destinations a second time and had no per-placement
// overrides; extending that twice is how bulk.ts happened.)

export const GBP_CTA_OPTIONS: ReadonlyArray<{ value: GbpCtaType; label: string }> = [
  { value: 'LEARN_MORE', label: 'Learn more' },
  { value: 'BOOK', label: 'Book' },
  { value: 'ORDER', label: 'Order' },
  { value: 'SHOP', label: 'Shop' },
  { value: 'SIGN_UP', label: 'Sign up' },
  { value: 'CALL', label: 'Call' },
];

type ClientMediaLike = { type: string; url: string; thumbnailUrl: string | null; altText: string | null };

/**
 * What the composer holds as media, from a stored post. ⚠️ The club is NOT given
 * the staff-side metadata (a Drive file name is Kyck's), so `source` and the file
 * fields are placeholders here and the SERVER restores the stored values on save
 * (server/modules/portalSocial/mediaRestore.ts), matched by url.
 */
export function clientMediaToPostMedia(m: ClientMediaLike): PostMedia {
  return {
    type: m.type === 'video' ? 'video' : 'image',
    url: m.url,
    thumbnailUrl: m.thumbnailUrl,
    altText: m.altText,
    source: 'upload',
    filename: null,
    mimeType: null,
    sizeBytes: null,
  };
}

export type ClientPostLike = {
  accountId: string;
  content: string;
  media: ClientMediaLike[];
  targets: Array<{
    platform: PostPlatform;
    fbContentType: string | null;
    igContentType: string | null;
    gbp: GbpConfig | null;
    customContent: string | null;
    customMedia: ClientMediaLike[];
  }>;
  scheduledFor: string | null;
  publishMode: string;
};

const fb = (v: string | null): FbContentType | null => (v === 'feed' || v === 'reel' || v === 'story' ? v : null);
const ig = (v: string | null): IgContentType | null => (v === 'feed' || v === 'reel' || v === 'story' ? v : null);

/** A stored club post, as the shared composer hydrates it. Unknown content types fall back to a plain post. */
export function composerSourceFromClientPost(post: ClientPostLike): ComposerSource {
  return {
    kyckAccountId: post.accountId,
    content: post.content,
    media: post.media.map(clientMediaToPostMedia),
    publishMode: post.publishMode === 'schedule' || post.publishMode === 'now' ? post.publishMode : 'draft',
    scheduledFor: post.scheduledFor,
    queueId: null,
    targets: post.targets.map((t) => ({
      platform: t.platform,
      fbContentType: fb(t.fbContentType),
      igContentType: ig(t.igContentType),
      gbp: t.gbp,
      customContent: t.customContent,
      customMedia: t.customMedia.map(clientMediaToPostMedia),
    })),
  };
}

export type ClubPostBody = Omit<PostInput, 'publishMode' | 'queueId'> & {
  publishMode: 'draft' | 'schedule' | 'now';
  confirmed?: boolean;
};

/**
 * The club's save body. All the override rules (only TICKED overrides are sent, a
 * ticked-but-empty one is not) live in the shared `toPostInput`; nothing here
 * re-decides them. A queue never applies to a club.
 */
export function toClubPostBody(
  state: ComposerState,
  mode: 'draft' | 'schedule' | 'now',
  scheduledForIso: string | null,
  confirmed?: boolean,
): ClubPostBody {
  const input = toPostInput({ ...state, publishMode: mode, scheduledFor: mode === 'schedule' ? scheduledForIso : null, queueId: null });
  return {
    kyckAccountId: input.kyckAccountId,
    content: input.content,
    media: input.media,
    targets: input.targets,
    scheduledFor: input.scheduledFor ?? null,
    publishMode: mode,
    ...(confirmed ? { confirmed: true } : {}),
  };
}
